import React, { useCallback, useEffect, useReducer } from 'react';
// temp disable - we need to remove moment at some point
// eslint-disable-next-line no-restricted-imports
import { parseDateTimeTz } from '@sparkpost/report-builder/helpers/date';
import { formatInTimeZone } from 'date-fns-tz';
import _ from 'lodash';
import { Grid, TextField } from 'src/components/matchbox';
import {
  getMomentPrecisionByDate,
  getRollupPrecision,
  getValidDateRange
} from 'src/helpers/metrics';
import styles from './ManualEntryFormV2.module.scss';

const DATE_PLACEHOLDER = '1970-01-20';
const TIME_PLACEHOLDER = '12:00am';
const DEBOUNCE = 500;

const initialState = {
  toDate: '',
  toTime: '',
  fromDate: '',
  fromTime: ''
};

const actionTypes = {
  fieldChange: 'FIELD_CHANGE',
  syncProps: 'SYNC'
};

const TIME_FORMAT = "h:mmaaaaa'm'";
const DATE_FORMAT = 'yyyy-MM-dd';

export function ManualEntryForm(props) {
  const [state, dispatch] = useReducer((state, { type, payload }) => {
    switch (type) {
      case actionTypes.syncProps: {
        const { to, from, timezone } = payload;

        return {
          ...state,
          toDate: formatInTimeZone(to, timezone, DATE_FORMAT),
          toTime: formatInTimeZone(to, timezone, TIME_FORMAT),
          fromDate: formatInTimeZone(from, timezone, DATE_FORMAT),
          fromTime: formatInTimeZone(from, timezone, TIME_FORMAT)
        };
      }
      case actionTypes.fieldChange: {
        return { ...state, ...payload };
      }
      default:
        return state;
    }
  }, initialState);

  const getPrecision = getRollupPrecision;

  const syncPropsToState = useCallback(({ to, from, timezone }) => {
    dispatch({
      type: actionTypes.syncProps,
      payload: { to, from, timezone }
    });
  }, []);

  useEffect(() => {
    syncPropsToState({ to: props.to, from: props.from, timezone: props.timezone });
  }, [syncPropsToState, props.to, props.from, props.timezone]);

  const handleFieldChange = (e) => {
    dispatch({ type: actionTypes.fieldChange, payload: { [e.target.id]: e.target.value } });
    debounceChanges();
  };

  const debounceChanges = useCallback(
    _.debounce(() => {
      validate();
    }, DEBOUNCE),
    []
  );

  const handleEnter = (e) => {
    if (e.key === 'Enter') {
      validate(e, true);
    }
  };

  const handleBlur = (e) => {
    validate(e, true);
  };

  function validate(e, shouldReset) {
    // first run the values are empty
    if (state.fromDate === '' && state.toDate === '') {
      return;
    }

    // allow for prop-level override of "now" (DI, etc.)
    const { now, roundToPrecision, preventFuture, defaultPrecision, timezone } = props;

    // TODO: remove parseDateTimeTz moment dependency
    const from = parseDateTimeTz(timezone, state.fromDate, state.fromTime).toDate();
    const to = parseDateTimeTz(timezone, state.toDate, state.toTime).toDate();

    try {
      const precision = getPrecision({ from, to, precision: defaultPrecision });
      const { to: roundedTo, from: roundedFrom } = getValidDateRange({
        from,
        to,
        now,
        roundToPrecision,
        preventFuture,
        precision
      });

      return props.selectDates({ to: roundedTo, from: roundedFrom, precision }, () => {
        if (e && e.key === 'Enter') {
          props.onEnter(e);
        }
      });
    } catch (e) {
      if (shouldReset) {
        syncPropsToState(props); // Resets fields if dates are not valid
      }
    }
  }

  const { toDate, toTime, fromDate, fromTime } = state;
  const { roundToPrecision, selectedPrecision, timezone } = props;

  let precisionLabel = null;
  let precisionLabelValue;
  let shouldDisableTime = ['day', 'week', 'month'].includes(selectedPrecision);

  const from = parseDateTimeTz(timezone, state.fromDate, state.fromTime);
  const to = parseDateTimeTz(timezone, state.toDate, state.toTime);

  if (roundToPrecision) {
    try {
      // allow for prop-level override of "now" (DI, etc.)
      const { now = new Date() } = props;
      const { from: validatedFrom, to: validatedTo } = getValidDateRange({
        from,
        to,
        now,
        roundToPrecision,
        precision: selectedPrecision
      });

      precisionLabelValue = getPrecision({ from: validatedFrom, to: validatedTo });
      if (!selectedPrecision) {
        shouldDisableTime = getMomentPrecisionByDate(validatedFrom, validatedTo) === 'days';
      }
    } catch (e) {
      precisionLabelValue = '';
    }

    precisionLabel = !selectedPrecision && (
      <div data-id="precision-label" className={styles.PrecisionLabel}>
        Precision: {_.startCase(_.words(precisionLabelValue).join(' '))}
      </div>
    );
  }

  return (
    <div onKeyDown={handleEnter} className={styles.DateFields}>
      <Grid middle="xs">
        <Grid.Column>
          <TextField
            data-id="fromDate"
            id="fromDate"
            label="From Date"
            labelHidden
            placeholder={DATE_PLACEHOLDER}
            onChange={handleFieldChange}
            onBlur={handleBlur}
            value={fromDate}
          />
        </Grid.Column>
        <Grid.Column>
          <TextField
            data-id="fromTime"
            id="fromTime"
            label="From Time"
            labelHidden
            placeholder={TIME_PLACEHOLDER}
            onChange={handleFieldChange}
            onBlur={handleBlur}
            value={fromTime}
            disabled={shouldDisableTime}
          />
        </Grid.Column>
        <Grid.Column>
          <TextField
            data-id="toDate"
            id="toDate"
            label="To Date"
            labelHidden
            placeholder={DATE_PLACEHOLDER}
            onChange={handleFieldChange}
            onBlur={handleBlur}
            value={toDate}
          />
        </Grid.Column>
        <Grid.Column>
          <TextField
            data-id="toTime"
            id="toTime"
            label="To Time"
            labelHidden
            placeholder={TIME_PLACEHOLDER}
            onChange={handleFieldChange}
            onBlur={handleBlur}
            value={toTime}
            disabled={shouldDisableTime}
          />
        </Grid.Column>
      </Grid>
      {precisionLabel}
    </div>
  );
}

export default ManualEntryForm;
